import React, {
	useMemo,
	useEffect,
	useState,
	useCallback,
} from 'react';
import {
	Space,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Values } from '@shared/utils/objectEnums';
import { Currencies } from '@shared/utils/constants';
import type {
	GetFixtureDetailsResponse,
	PeriodSchedule,
} from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { UpdatePeriodScheduleItemRequest } from '@api/features/fixtures/updatePeriodScheduleItem';
import TcTerms from '@api/models/tc-terms';
import {
	createPeriodScheduleItem,
	deletePeriodScheduleItem,
	updatePeriodScheduleItem,
} from '@client/lib/api';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import showErrorNotification from '@client/utils/showErrorNotification';
import EditableCellTableRedux from '@client/components/EditableTableRedux/EditableCellTableRedux';
import Button from '@client/components/Button';
import SavingIndicator from '@client/components/SavingIndicator';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import Modal from '@client/components/Modal/Modal';
import { getModalTableColumns } from './periodScheduleHelpers';

type Row = NonNullable<PeriodSchedule>;

const pickAttributes = (row: Row): UpdatePeriodScheduleItemRequest['attributes'] => ({
	hireRate: row.hireRate,
	durationMinimumValue: row.durationMinimumValue,
	durationMinimumUnit: row.durationMinimumUnit,
	durationMinimumVariance: row.durationMinimumVariance,
	minimumDurationExact: row.minimumDurationExact,
	durationMaximumValue: row.durationMaximumValue,
	durationMaximumUnit: row.durationMaximumUnit,
	durationMaximumVariance: row.durationMaximumVariance,
	maximumDurationExact: row.maximumDurationExact,
	periodType: row.periodType,
	hireType: row.hireType,
	description: row.description,
	exercised: row.exercised,
	useExactDate: row.useExactDate,
});

const PeriodScheduleModal = ({
	setPeriodScheduleDrawerOpen,
	periodScheduleDrawerOpen,
	fixture,
	fixtureCurrency,
	refreshFixture,
	afterSave,
	periodSchedule,
	disabled = false,
	tcTerms,
}: {
	setPeriodScheduleDrawerOpen: (open: boolean) => void;
	periodScheduleDrawerOpen: number | boolean;
	fixture: GetFixtureDetailsResponse<TcFixtureProps> | undefined;
	fixtureCurrency: Values<typeof Currencies>;
	refreshFixture: () => void;
	afterSave?: () => void;
	periodSchedule: PeriodSchedule[];
	disabled?: boolean;
	tcTerms: TcTerms
}) => {
	const [saving, setSaving] = useState(false);
	const [draft, setDraft] = useState<Row[]>(periodSchedule ?? []);
	const [dirtyById, setDirtyById] = useState<Map<number, Row>>(new Map());

	useEffect(() => {
		if (!periodScheduleDrawerOpen) {
			return;
		}

		setDraft(periodSchedule ?? []);
		setDirtyById(new Map());
	}, [periodScheduleDrawerOpen]);

	const mergeWithDirty = useCallback((serverRows: Row[], dirty: Map<number, Row>) => {
		return serverRows.map((r) => dirty.get(r.id) ?? r);
	}, []);

	useEffect(() => {
		if (!periodScheduleDrawerOpen) {
			return;
		}

		const serverRows = periodSchedule ?? [];

		setDraft(mergeWithDirty(serverRows, dirtyById));
	}, [periodSchedule, periodScheduleDrawerOpen, dirtyById, mergeWithDirty]);

	const onClose = useCallback(() => {
		setPeriodScheduleDrawerOpen(false);
	}, [setPeriodScheduleDrawerOpen]);

	const mostRecentEntry = useMemo(() => {
		if (periodSchedule == null) {
			return null;
		}

		return periodSchedule[periodSchedule.length - 1];
	}, [periodSchedule]);

	const isOpen = Boolean(periodScheduleDrawerOpen);

	const addNewPeriod = useCallback(async () => {
		if (!fixture || !mostRecentEntry) {
			return;
		}

		const {
			id: _id,
			from: _from,
			to: _to,
			maxTo: _maxTo,
			order: _order,
			...entryData
		} = mostRecentEntry;

		try {
			await createPeriodScheduleItem({
				...entryData,
				hireRate: 0,
				maximumDurationExact: null,
				minimumDurationExact: null,
				tcTermsId: tcTerms.id,
				useExactDate: false,
			});
			await refreshFixture();
			await afterSave?.();

			showSuccessNotification('Succesfully added new period');
		} catch (e) {
			showErrorNotification('Could not create period', e as Error);
		}
	}, [afterSave, fixture, mostRecentEntry, refreshFixture, tcTerms.id]);

	const deleteScheduleItem = useCallback(async (id: number) => {
		if (!fixture) {
			return;
		}

		try {
			await deletePeriodScheduleItem(id);
			await refreshFixture();
			await afterSave?.();

			showSuccessNotification('Succesfully deleted period');
		} catch (e) {
			showErrorNotification('Could not delete period', e as Error);
		}
	}, [afterSave, fixture, refreshFixture]);

	const onRowChange = useCallback((row: Row) => {
		setDraft(prev => prev.map(r => (r.id === row.id ? row : r)));
		setDirtyById(prev => {
			const next = new Map(prev);
			next.set(row.id, row);

			return next;
		});
	}, []);

	const patchDraftRow = useCallback((id: number, patch: Partial<Row>) => {
		setDraft(prev => {
			const next = prev.map(r => (r.id === id ? { ...r, ...patch } : r));
			const nextRow = next.find(r => r.id === id);

			if (nextRow) {
				setDirtyById(prevDirty => {
					const m = new Map(prevDirty);
					m.set(id, nextRow);

					return m;
				});
			}

			return next;
		});
	}, []);

	const handleSave = useCallback(async () => {
		if (!fixture || dirtyById.size === 0) {
			return;
		}

		try {
			setSaving(true);

			const entries = [...dirtyById.entries()];

			for (const [id, row] of entries) {
				await updatePeriodScheduleItem(id, pickAttributes(row));
			}

			setDirtyById(new Map());
			await refreshFixture();
			await afterSave?.();

			showSuccessNotification('Period schedule saved');
		} catch (e) {
			showErrorNotification('Could not save period schedule', e as Error);
			await refreshFixture();
		} finally {
			setSaving(false);
		}
	}, [afterSave, fixture, dirtyById, refreshFixture]);

	const columns = useMemo(
		() =>
			getModalTableColumns({
				disabled,
				fixtureCurrency,
				periodSchedule: draft,
				tcTerms,
				deleteScheduleItem,
				patchDraftRow,
			}),
		[disabled, fixtureCurrency, draft, tcTerms, deleteScheduleItem, patchDraftRow],
	);

	const hasPendingChanges = isOpen && dirtyById.size > 0;

	const saveButton = useSaveButton({
		onSave: handleSave,
		loading: saving,
		hasPendingChanges,
		visible: isOpen,
		title: 'Save schedule',
	});

	return (
		<Modal
			title="Period Schedule"
			onCancel={onClose}
			onClose={onClose}
			footer={false}
			width="98%"
			// @ts-ignore
			open={periodScheduleDrawerOpen}
		>
			<Space style={{ marginBottom: 8 }}>
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={addNewPeriod}
				>
					Add period
				</Button>

				{saveButton}

				<SavingIndicator spinning={saving} />
			</Space>
			<EditableCellTableRedux
				size="small"
				rowKey="id"
				pagination={false}
				dataSource={draft}
				onRowChange={onRowChange}
				// @ts-ignore
				columns={columns}
			/>
		</Modal>
	);
};

export default PeriodScheduleModal;
